import { useEffect } from "react";
import { CreatedEvent } from "../redux/features/event/event.types";
import { useAppSelector } from "../redux/hooks";
import {
  eventCheckInPermissions,
  eventCreatorPermissions,
  eventManagerPermissions,
} from "../utils/constants";
import { EventPermissions } from "../utils/types";

export const useEventPermissions = ({
  event,
  setPermissions,
}: {
  event?: CreatedEvent;
  setPermissions: (p: EventPermissions[]) => void;
}) => {
  const { profile } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (event) {
      const isCoOrganizer = (event.coOrganizers ?? [])
        .map((c) => c.email)
        .includes(profile?.email!);
      const coOrganizerDetails = isCoOrganizer
        ? (event.coOrganizers ?? []).find((c) => c.email === profile?.email)
        : undefined;

      let permissions: EventPermissions[] = [];
      const baseEventActions: EventPermissions[] = [
        "editEventDetails",
        "addTicket",
        "deleteTicket",
        "editTicketDetails",
        "checkInGuests",
        "assignDiscounts",
        "sendEmailBlasts",
        "addCoorganizers",
        "manageCoorganizers",
      ];

      // Role based permission definition
      if (event.userType === "organiser") {
        permissions = eventCreatorPermissions;
      } else if (
        isCoOrganizer &&
        !!coOrganizerDetails &&
        coOrganizerDetails.role === "check-in-only"
      ) {
        permissions = eventCheckInPermissions;
      } else if (
        isCoOrganizer &&
        !!coOrganizerDetails &&
        coOrganizerDetails.role === "manager"
      ) {
        permissions = eventManagerPermissions;
      }

      // Event status based permission
      if (event.isFlagged || event.status === "flagged") {
        permissions = permissions.filter((p) =>
          ["viewCoorganizers", "viewGuestList", "viewSales"].includes(p),
        );
      } else if (event.status === "completed") {
        permissions = permissions.filter(
          (p) => ![...baseEventActions, "cancelEvent"].includes(p),
        );
      } else if (event.status === "draft") {
        permissions = permissions.filter((p) =>
          [
            "editEventDetails",
            "editTicketDetails",
            "addTicket",
            "deleteTicket",
            "deleteEvent",
          ].includes(p),
        );
      } else if (event.status === "cancelled") {
        permissions = permissions.filter(
          (p) => ![...baseEventActions, "cancelEvent"].includes(p),
        );
      } else if (event.status === "archived") {
        permissions = permissions.filter((p) =>
          [
            "archiveEvent",
            "viewCoorganizers",
            "viewGuestList",
            "viewSales",
          ].includes(p),
        );
      }

      setPermissions(permissions);
    }
    // eslint-disable-next-line
  }, [event]);

  return undefined;
};
